import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTickets, getUtentiRete } from '../../services/ticketsApi';
import { segui, giro, statoCopertura } from '../../services/flussoGiocate';
import { caricaProfilo } from '../../services/profiloDiGioco';
import { statoDi, typeLabels, accettataDaPoco, RICEVUTA_ACCETTATA } from '../../utils/statiGiocata';
import { daLampeggiare, segnaRipropostaVista } from '../../utils/riproposteViste';
import { restante, useAdesso } from '../../utils/contoAllaRovescia';
import { haLaVoce } from '../../utils/permessi';
import i18next from 'i18next';
import {lingua} from '../../preferenze';

/**
 * LE ULTIME GIOCATE, SOTTO LA SCHEDINA.
 *
 * Sta sotto il tasto di invio, nella stessa colonna, e dice COM'E' ANDATA:
 * quello che si sta componendo (sopra) e quello che e' gia' partito (qui)
 * sono due cose, e mescolarle fa premere il tasto sbagliato.
 *
 * COSA FA
 *   Mostra le ultime N giocate -- N e quante lo decide il backoffice (Vincoli
 *   › Impostazioni schedina, sezione 6), e arriva nel profilo di gioco come
 *   `ultimeGiocate {attive, quante}`. Un giocatore vede le sue; un'agenzia
 *   quelle dei suoi giocatori, con l'username davanti. Il filtro lo fa il
 *   SERVIZIO sul sottoalbero di chi chiede: qui non si manda nessun ramo,
 *   quindi non si puo' vedere ne' sopra ne' di lato.
 *   L'unica riga su cui si fa qualcosa e' una RIPROPOSTA: lampeggia finche'
 *   non la si apre, porta il tempo che resta e il tasto che porta al ticket,
 *   dove si accetta o si rifiuta.
 *
 * COSA NON FA
 *   Non ha filtri, ricerca, pagine: per quello c'e' «tutte», che porta alla
 *   lista scommesse. Non si annulla e non si modifica niente da qui, e non si
 *   risponde a una riproposta da qui: i numeri da leggere prima di dire si'
 *   stanno nel ticket. Senza accesso non compare: un ospite non ha giocate.
 *   Se il profilo non porta i due campi, non compare e tace -- nessun valore
 *   inventato da questa parte.
 *
 * COME SI AGGIORNA, oggi: chiede di nuovo le righe ogni 5 secondi finche'
 * c'e' una giocata in riserva o riproposta, ogni 30 altrimenti, mai a scheda
 * nascosta, e subito dopo una giocata mandata o una risposta (evento
 * `giocataCambiata` sulla finestra). Quando il servizio avra' il flusso del
 * giocatore (la spinta GIOCATA_CAMBIATA di tempo-reale.md) questo giro si
 * sostituisce con quello: le righe e le parole restano le stesse.
 */

const cifra = (n) => (n == null || Number.isNaN(Number(n))
  ? '—'
  : Number(n).toLocaleString(lingua(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const due = (n) => String(n).padStart(2, '0');

/** Quando: «2 min fa» per le cose fresche, la data per il resto. */
function quando(iso, adesso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const min = Math.floor((adesso - d.getTime()) / 60000);
  if (min < 1) return 'adesso';
  if (min < 60) return `${min} min fa`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} h fa`;
  return `${due(d.getDate())}/${due(d.getMonth() + 1)} ${due(d.getHours())}:${due(d.getMinutes())}`;
}

const COME_SI_LEGGE = { CAMBIO_QUOTA: 'Cambio quota', CAMBIO_IMPORTO: 'Cambio importo' };
function tipoRiproposta(r) {
  const t = Array.isArray(r && r.tipi) ? r.tipi : [];
  if (t.length > 1) return 'Cambio quota e importo';
  return COME_SI_LEGGE[t[0]] || 'Riproposta';
}

function descriviErrore(e) {
  const d = e && e.response && e.response.data;
  const codice = (d && d.code) || (e && e.response && e.response.status) || 'RETE';
  const testo = (d && d.message) || (e && e.message) || i18next.t('Il servizio non ha risposto');
  return `${testo} · ${codice}`;
}

function sistemaOTipo(g) {
  const base = typeLabels[g.type] || g.type || '';
  return g.eventsCount > 1 ? `${base} · ${g.eventsCount} eventi` : base;
}

const RISERVA_VIVA = ['DA_ACCETTARE', 'RIPROPOSTA'];
const POSTA_TORNATA = ['RIFIUTATA', 'SCADUTA', 'VOIDED'];

export default function UltimeGiocate({ profilo, chi, io, versione }) {
  const navigate = useNavigate();
  const agenziaChiede = chi === 'agenzia';

  /* DI CHI E' L'IMPOSTAZIONE. Per un giocatore e' nel suo profilo, che la
     schedina ha gia' in mano. Per un'AGENZIA no: la schedina carica il
     profilo del giocatore scelto nella tendina (o quello della skin finche'
     non sceglie nessuno), mentre questo elenco e' dell'agenzia -- e il
     backoffice lo scrive sul suo nodo. Quindi l'agenzia legge il PROPRIO
     profilo, una volta: il servizio lo concede, perche' la catena di un nodo
     contiene il nodo stesso. */
  const [profiloMio, setProfiloMio] = useState(null);
  useEffect(() => {
    if (!agenziaChiede || !io) return undefined;
    let vivo = true;
    caricaProfilo(io).then((p) => { if (vivo) setProfiloMio(p); });
    return () => { vivo = false; };
  }, [agenziaChiede, io]);

  const sorgente = agenziaChiede ? profiloMio : profilo;
  const impostazione = sorgente && sorgente.ultimeGiocate;
  const attive = !!(impostazione && impostazione.attive);
  const quante = Number(impostazione && impostazione.quante) || 0;
  const agenzia = chi === 'agenzia';
  const mostra = attive && quante > 0 && (chi === 'giocatore' || agenzia);

  const [righe, setRighe] = useState([]);
  const [errore, setErrore] = useState(null);
  const [caricate, setCaricate] = useState(false);
  const [utenti, setUtenti] = useState({});
  const [, ridisegna] = useState(0);

  const ricarica = useCallback(async () => {
    if (!mostra) return;
    try {
      const d = await getTickets({ page: 1, limit: quante });
      setRighe(d.tickets || []);
      /* Le giocate in attesa fra queste hanno un flusso: lo apre e lo
         chiude il modulo, qui si dice solo cosa si sta mostrando. */
      segui('striscia', d.tickets || []);
      setErrore(null);
    } catch (e) {
      setErrore(descriviErrore(e));
    } finally {
      setCaricate(true);
    }
  }, [mostra, quante]);

  /* All'apertura, e ogni volta che la schedina dice che qualcosa e' partito. */
  useEffect(() => { ricarica(); }, [ricarica, versione]);

  /* Gli username dei giocatori, una volta sola: il ticket porta solo
     l'identificativo, e l'agenzia deve leggere un nome. */
  useEffect(() => {
    if (!mostra || !agenzia) return undefined;
    let vivo = true;
    getUtentiRete().then((elenco) => {
      if (!vivo) return;
      const mappa = {};
      (elenco || []).forEach((u) => { if (u && u.userId) mappa[u.userId] = u.username; });
      setUtenti(mappa);
    }).catch(() => { /* resta l'identificativo accorciato */ });
    return () => { vivo = false; };
  }, [mostra, agenzia]);

  const vivo = righe.some((g) => RISERVA_VIVA.includes(g.status));

  /* IL GIRO A TEMPO, a due passi: lento (30 s) finche' ogni giocata in
     attesa ha il suo flusso vivo -- la spinta GIOCATA_CAMBIATA arriva da
     quello, come evento `giocataCambiata` -- e veloce (5 s) solo quando una
     giocata aspetta senza flusso. Quando nessuna aspetta, lento e basta. */
  useEffect(() => {
    if (!mostra) return undefined;
    const ferma = giro(ricarica, { veloce: 5000, lento: 30000 });
    const alRitorno = () => { if (!document.hidden) ricarica(); };
    const alCambio = () => ricarica();
    const allaVista = () => ridisegna((n) => n + 1);
    document.addEventListener('visibilitychange', alRitorno);
    window.addEventListener('giocataCambiata', alCambio);
    window.addEventListener('ripropostaVista', allaVista);
    window.addEventListener('flussoCambiato', allaVista);
    return () => {
      ferma();
      document.removeEventListener('visibilitychange', alRitorno);
      window.removeEventListener('giocataCambiata', alCambio);
      window.removeEventListener('ripropostaVista', allaVista);
      window.removeEventListener('flussoCambiato', allaVista);
    };
  }, [mostra, ricarica]);

  /* Allo smontaggio (o se la striscia si spegne) questo pezzo non guarda
     piu' niente: i flussi che teneva aperti solo lui si chiudono. */
  useEffect(() => () => segui('striscia', []), []);
  useEffect(() => { if (!mostra) segui('striscia', []); }, [mostra]);

  const adesso = useAdesso(mostra && vivo);

  if (!mostra) return null;

  const apri = (g) => {
    if (g.status === 'RIPROPOSTA') segnaRipropostaVista(g.ticketCode);
    navigate(`/my-account/lista-scommesse?codice=${encodeURIComponent(g.ticketCode || '')}`);
  };

  const nomeDi = (g) => utenti[g.userId] || (g.userId ? `${String(g.userId).slice(0, 8)}…` : '');

  return (
    <section className="lvc-ultime" aria-label={i18next.t('Ultime giocate')}>
      <div className="lvc-ultime-testa">
        <span>{agenzia ? i18next.t('Ultime giocate dei tuoi giocatori') : i18next.t('Le tue ultime giocate')}</span>
        {/* LA SPIA DEL CANALE, solo quando c'e' qualcosa da coprire: «in
            diretta» se ogni giocata in attesa ha il suo flusso vivo, «ogni 5 s»
            se la pagina sta andando a giro perche' il flusso non c'e'. Dice
            come arrivano le notizie, non se ce ne sono. */}
        {(() => {
          const stato = statoCopertura();
          if (stato === 'niente') return null;
          return (
            <span className={'lvc-ultime-canale' + (stato === 'diretta' ? ' lvc-ultime-canale--diretta' : '')}
              title={stato === 'diretta'
                ? i18next.t('Le notizie su questa giocata arrivano nell’istante in cui succedono')
                : i18next.t('Il canale in diretta non e’ aperto: la striscia si aggiorna ogni 5 secondi')}>
              {stato === 'diretta' ? i18next.t('in diretta') : i18next.t('ogni 5 s')}
            </span>
          );
        })()}
        {/* «tutte» porta a Lista scommesse: solo a chi ha la sua voce. */}
        {haLaVoce('SITE_BETS_VIEW') && (
          <button type="button" className="lvc-ultime-tutte"
            onClick={() => navigate('/my-account/lista-scommesse')}>{i18next.t('tutte →')}</button>
        )}
      </div>

      {errore && <div className="lvc-ultime-errore">{i18next.t('Le giocate non si sono caricate ·')}{' '}{errore}</div>}

      {caricate && !errore && righe.length === 0 && (
        <div className="lvc-ultime-nota">
          {agenzia ? i18next.t('Nessuna giocata dei tuoi giocatori, ancora.') : i18next.t('Nessuna giocata, ancora: la prima la vedi qui.')}
        </div>
      )}

      <div className="lvc-ultime-righe">
        {righe.map((g) => {
          /* La ricevuta «Accettata» vive solo qui: nella lista scommesse la
             stessa giocata e' «In corso» (vedi statiGiocata.js). */
          const st = accettataDaPoco(g) ? RICEVUTA_ACCETTATA : statoDi(g);
          const r = g.riproposta;
          const riproposta = g.status === 'RIPROPOSTA' && r;
          const lampeggia = daLampeggiare(g);
          const vinta = ['WON', 'PAID', 'AWAITING_PAYMENT'].includes(st.chiave);
          const piuColonne = (Number(g.combinazioni) || 1) > 1;

          let destra;
          if (riproposta) destra = <span className="lvc-ultime-tempo">{restante(r.scadeIl, adesso)}</span>;
          else if (g.status === 'DA_ACCETTARE') destra = <span className="lvc-ultime-quando">{i18next.t('scade fra')}{' '}{restante(g.scadeIl, adesso) || '—'}</span>;
          else if (st.chiave === 'PAID') destra = <span className="lvc-ultime-spunta">{i18next.t('✓ accreditata')}</span>;
          else if (POSTA_TORNATA.includes(g.status)) destra = <span className="lvc-ultime-spunta">{i18next.t('posta tornata')}</span>;
          else destra = <span className="lvc-ultime-quando">{quando(st.chiave === 'ACCETTATA' ? g.accettataIl : g.createdAt, adesso)}</span>;

          let soldi;
          if (st.chiave === 'LOST' || POSTA_TORNATA.includes(g.status)) soldi = cifra(g.stake);
          else if (vinta) soldi = <>{cifra(g.stake)} → <b className="lvc-ultime-verde">{cifra(g.payout || g.potentialWin)}</b></>;
          else if (riproposta) soldi = <b>{cifra(r.importoTotale != null ? r.importoTotale : g.stake)}</b>;
          else soldi = <>{cifra(g.stake)} → {piuColonne ? 'max ' : ''}{cifra(g.potentialWin)}</>;

          return (
            <button
              type="button"
              key={g.id || g.ticketCode}
              className={'lvc-ultime-riga'
                + (riproposta ? ' lvc-ultime-riga--accesa' : '')
                + (lampeggia ? ' lvc-lampeggia' : '')}
              onClick={() => apri(g)}
            >
              <div className="lvc-ultime-r1">
                <span className={'lvc-ultime-pallino' + (lampeggia ? ' lvc-lampeggia-pallino' : '')}
                  style={{ background: st.colore }} />
                <span className={'lvc-ultime-stato' + (riproposta ? ' lvc-ultime-stato--accesa' : '')}
                  style={riproposta ? undefined : { color: st.chiave === 'PENDING' ? 'var(--lvc-text-2)' : st.colore }}>
                  {riproposta && agenzia ? i18next.t('Riproposta a') : st.etichetta}
                </span>
                {agenzia && <span className="lvc-ultime-chi">{nomeDi(g)}</span>}
                <span className="lvc-ultime-spazio" />
                {destra}
              </div>
              <div className="lvc-ultime-r2">
                <span className="lvc-ultime-codice">{g.ticketCode}</span>
                <span>{sistemaOTipo(g)}</span>
                <span className="lvc-ultime-spazio" />
                <span className="lvc-ultime-soldi">{soldi}</span>
              </div>
              {riproposta && (
                <>
                  <div className="lvc-ultime-cambia">
                    {tipoRiproposta(r)}{i18next.t(': vincita massima')}{' '}
                    <s>{cifra(g.potentialWin)}</s> → <b>{cifra(r.vincitaMassima)}</b>
                    {Number(r.bonus) === 0 && Number(g.bonusMax) > 0
                      ? <>{' '}{i18next.t('· cade il bonus (−')}{cifra(g.bonusMax)})</> : null}
                  </div>
                  <span className="lvc-ultime-tasto">
                    {agenzia ? i18next.t('Guarda e rispondi per {{v0}}', {v0: nomeDi(g)}) : i18next.t('Guarda e rispondi')}
                  </span>
                </>
              )}
              {g.status === 'DA_ACCETTARE' && (
                <div className="lvc-ultime-sotto">{i18next.t('Sta guardando il banco. Non devi fare niente.')}</div>
              )}
            </button>
          );
        })}
      </div>

      {righe.length > 0 && (
        <div className="lvc-ultime-nota">
          {i18next.t('Le ultime')}{' '}{Math.min(quante, righe.length)} {agenzia ? i18next.t('giocate dei tuoi giocatori') : 'giocate'}{i18next.t(', dalla più recente. Si aggiornano da sole. Tocca una riga per aprirla; per cercare e filtrare vai su')}{' '}<b>{i18next.t('tutte')}</b>{i18next.t('. L’unica riga su cui si fa qualcosa è quella accesa.')}
        </div>
      )}
    </section>
  );
}
